import React, { useRef, useState } from 'react';
import styles from './styles.module.scss';

const PLACEHOLDERS = ['2', '0', '1', '2', '?'];

interface ZipCodeCellsProps {
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
}

const ZipCodeCells: React.FC<ZipCodeCellsProps> = ({ value, onChange, invalid = false }) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const digits = value.replace(/\D/g, '').slice(0, 5);

  const write = (next: string) => {
    onChange(next.replace(/\D/g, '').slice(0, 5));
  };

  const focusAt = (index: number) => {
    const target = Math.max(0, Math.min(4, index));
    refs.current[target]?.focus();
  };

  return (
    <div className={styles.digits} role="group" aria-label="CAP">
      <input type="hidden" name="zip" value={digits} />
      {PLACEHOLDERS.map((placeholder, index) => (
        <input
          key={placeholder + index}
          ref={(node) => {
            refs.current[index] = node;
          }}
          className={`${styles.digit} ${focusedIndex === index ? styles.digitOn : ''} ${
            invalid ? styles.digitError : ''
          }`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={1}
          aria-label={`Cifra ${index + 1} di 5`}
          aria-invalid={invalid}
          placeholder={placeholder}
          value={digits[index] || ''}
          onFocus={(event) => {
            setFocusedIndex(index);
            event.currentTarget.select();
          }}
          onChange={(event) => {
            const digit = event.target.value.replace(/\D/g, '').slice(-1);
            if (!digit) return;
            const next = (digits.slice(0, index) + digit + digits.slice(index + 1)).slice(0, 5);
            write(next);
            if (index < 4) focusAt(index + 1);
          }}
          onKeyDown={(event) => {
            if (/^\d$/.test(event.key)) {
              event.preventDefault();
              const next = (digits.slice(0, index) + event.key + digits.slice(index + 1)).slice(0, 5);
              write(next);
              if (index < 4) focusAt(index + 1);
              return;
            }
            if (event.key === 'Backspace') {
              event.preventDefault();
              if (digits[index]) {
                write(digits.slice(0, index) + digits.slice(index + 1));
              } else if (index > 0) {
                write(digits.slice(0, index - 1) + digits.slice(index));
                focusAt(index - 1);
              }
              return;
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              focusAt(index - 1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              focusAt(index + 1);
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
            write(pasted);
            focusAt(Math.min(pasted.length, 4));
          }}
        />
      ))}
    </div>
  );
};

export default ZipCodeCells;
